import { SxProps, Theme } from "@mui/material";

import { theme } from "@/src/theme/theme";
import { heroGradientColors, radius } from "@/src/theme/tokens";

import { VehicleType } from "@/src/types/types";

export const HERO_GRADIENT_BACKGROUND_IMAGE = [
  `radial-gradient(ellipse 70% 80% at 20% 30%, ${heroGradientColors.accent} 0%, transparent 60%),`,
  `radial-gradient(ellipse 55% 70% at 80% 80%, ${heroGradientColors.deep} 0%, transparent 65%),`,
  `linear-gradient(135deg, ${heroGradientColors.mid} 0%, ${heroGradientColors.base} 100%)`
].join(" ");

export const hasVehicleTypesSubtitle = (vehicleType: VehicleType): boolean =>
  vehicleType === VehicleType.MOTO || vehicleType === VehicleType.CARAVAN;

export const heroRootSx: SxProps<Theme> = {
  backgroundColor: theme.palette.background.default,
  backgroundSize: "cover",
  width: "100%",
  overflow: "hidden"
};

export const heroCanvasSx: SxProps<Theme> = {
  position: "relative",
  width: "100%",
  backgroundColor: theme.palette.brand.tertiary,
  height: {
    xs: "auto",
    md: "clamp(260px, calc(140px + 26vw), 460px)"
  },
  boxSizing: "border-box",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column"
};

export const heroContentSx: SxProps<Theme> = {
  display: { xs: "flex", md: "block" },
  flexDirection: "column",
  alignItems: "center",
  width: "100%",
  maxWidth: { md: "lg" },
  mx: { md: "auto" },
  px: { md: 4 },
  pt: { xs: "24px", sm: "40px", md: 0 },
  zIndex: 2,
  position: { xs: "relative", md: "absolute" },
  left: { md: "50%" },
  top: { md: 0 },
  bottom: { md: 0 },
  transform: { md: "translateX(-50%)" },
  boxSizing: "border-box"
};

export const heroTitleCardWrapperSx: SxProps<Theme> = {
  position: { xs: "static", md: "absolute" },
  left: {
    md: "clamp(96px, calc(32vw - 192px), 224px)"
  },
  top: { md: "50%" },
  transform: {
    md: "translateY(-80%)"
  },
  zIndex: 2,
  width: {
    xs: "clamp(260px, 70vw, 520px)",
    sm: "clamp(360px, 62vw, 720px)",
    md: "clamp(443px, calc(12.25vw + 332px), 492px)"
  },
  boxSizing: "border-box",
  flexShrink: 0
};

export const heroTitleCardSx: SxProps<Theme> = {
  width: "100%",
  aspectRatio: { xs: "auto", md: "600 / 234" },
  minHeight: {
    xs: "calc(clamp(260px, 70vw, 520px) * 4 / 9 + 0.75rem)",
    sm: "calc(clamp(360px, 62vw, 720px) * 4 / 9 + 0.75rem)",
    md: "unset"
  },
  px: {
    xs: "clamp(12px, 3vw, 24px)",
    sm: "clamp(24px, 4.5vw, 48px)",
    md: "32px"
  },
  py: {
    xs: "clamp(20px, 5vw, 36px)",
    sm: "clamp(8px, 2.2vw, 18px)",
    md: "24px"
  },
  backgroundColor: heroGradientColors.card,
  backgroundImage: HERO_GRADIENT_BACKGROUND_IMAGE,
  borderRadius: radius.hero,
  textAlign: "center",
  overflow: "hidden",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center"
};

export const heroTitleTextSx: SxProps<Theme> = [
  theme.typography.h1,
  {
    // Heading layout adds margin/padding for page rhythm; reset in the card.
    m: 0,
    p: 0,
    // Stacked title lines; tighter than default h1 line-height.
    lineHeight: 1.15,
    width: "100%",
    maxWidth: "100%",
    textAlign: "center"
  }
];

export const heroTitleMainSpanSx: SxProps<Theme> = {
  display: "block",
  fontStyle: "normal",
  color: theme.palette.text.primary,
  wordSpacing: { xs: "100vw", sm: "normal" }
};

export const heroTitleEmphasisSpanSx: SxProps<Theme> = {
  display: "block",
  fontStyle: "italic",
  color: theme.palette.common.white,
  maxWidth: { xs: "none", md: "8ch" },
  mx: "auto",
  wordSpacing: { xs: "100vw", md: "normal" }
};

export const heroVehicleImageSx: SxProps<Theme> = {
  position: { xs: "static", md: "absolute" },
  left: { md: "50%" },
  top: { md: "50%" },
  transform: {
    md: "translate(-23%, -52%)"
  },
  width: "100%",
  maxWidth: {
    xs: "clamp(420px, 78vw, 680px)",
    sm: "clamp(360px, 62vw, 720px)",
    md: "clamp(450px, 50vw, 730px)"
  },
  height: "auto",
  display: "block",
  mt: {
    xs: "calc(-4% - 2rem)",
    sm: "calc(-4% - 3rem)",
    md: 0
  },
  pointerEvents: "none",
  userSelect: "none",
  zIndex: 3
};

export const heroSelectorSectionSx: SxProps<Theme> = {
  position: "relative",
  zIndex: 2,
  maxWidth: "lg",
  mx: "auto",
  width: "100%",
  flex: { xs: "0 0 auto", md: 1 },
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: { xs: "flex-start", md: "flex-end" },
  boxSizing: "border-box",
  px: { xs: 0, md: 4 }
};

export const heroSelectorRowSx: SxProps<Theme> = {
  position: "relative",
  zIndex: 3,
  display: "flex",
  justifyContent: "center",
  width: "100%",
  minWidth: 0,
  boxSizing: "border-box",
  px: { xs: 1.5, sm: 0 },
  flexShrink: 0
};

export const heroDividerSx: SxProps<Theme> = {
  position: "relative",
  zIndex: 0,
  flexShrink: 0,
  height: "1px",
  width: "100%",
  mt: "-1px",
  backgroundColor: theme.palette.misc.outlineBorder
};

export const instructionsRowSx: SxProps<Theme> = [
  theme.typography.h3,
  {
    display: "flex",
    flexDirection: ["column", "column", "row"],
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "transparent",
    maxWidth: "lg",
    mx: "auto",
    pt: [0.75, 2.5, 3],
    px: [3, 4, 0],
    gap: [0, 2, 9],
    ol: {
      display: "flex",
      flexDirection: "column",
      listStyleType: "none",
      counterReset: "item",
      paddingLeft: [0, 3, 0],
      mb: 0,
      width: ["fit-content", "fit-content", 420],
      maxWidth: ["100%", "100%", "100%"],
      flexShrink: 0,
      alignSelf: ["center", "center", "auto"],
      mx: ["auto", "auto", 0]
    },
    li: {
      display: "flex",
      alignItems: "flex-start",
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: "1.5rem",
      fontSize: ["1.125rem", "1.25rem"],
      mb: 1.5,
      "&:before": {
        content: "counter(item)",
        mb: 0.75,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: theme.palette.semantic.primary.main,
        counterIncrement: "item",
        border: `2px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        borderRadius: "50%",
        mr: 1.5,
        mt: "-0.25rem",
        width: "2rem",
        height: "2rem",
        lineHeight: 1
      }
    }
  }
];

export const instructionsSubtitleSx: SxProps<Theme> = {
  pr: [5, 0],
  maxWidth: ["100%", "100%", 320]
};

export const ctaButtonWrapperSx: SxProps<Theme> = {
  maxWidth: ["100%", "100%", 350],
  mt: [1.5, 0, 0]
};

export const ctaButtonSx: SxProps<Theme> = {
  backgroundColor: theme.palette.semantic.primary.main,
  width: ["300px", "300px", "100%"],
  minWidth: [180, 250],
  height: 48,
  "&.MuiButton-outlined.MuiButton-colorPrimary:hover": {
    backgroundColor: theme.palette.misc.coldBackground,
    boxShadow: theme.shadows[2]
  }
};
